/* cc-carousel-bar — draggable scroll indicator for Dawn slider-component
   carousels (Tony 2026-07-11, Nike reference). Custom element placed
   inside the section markup: connectedCallback re-fires on every
   ProductRecommendations innerHTML swap = sanctioned re-render hook
   (cc-cart-drawer-hub pattern, NO MutationObserver). ResizeObserver on
   the <ul> re-syncs when cc-related-more appends items or the viewport
   changes (RO is Dawn-sanctioned — the MutationObserver ban stands). */

(() => {
  if (customElements.get('cc-carousel-bar')) return;

  class CCCarouselBar extends HTMLElement {
    connectedCallback() {
      this.innerHTML =
        '<div class="cc-cbar__track"><div class="cc-cbar__thumb"></div></div>';
      this.track = this.querySelector('.cc-cbar__track');
      this.thumb = this.querySelector('.cc-cbar__thumb');
      const host = this.closest('slider-component');
      this.slider = host ? host.querySelector('ul.slider') : null;
      if (!this.slider) {
        this.classList.add('cc-cbar--idle');
        return;
      }
      this._onScroll = () => this.requestSync();
      this.slider.addEventListener('scroll', this._onScroll, { passive: true });
      this._ro = new ResizeObserver(() => this.requestSync());
      this._ro.observe(this.slider);
      /* content growth (cc-related-more) changes scrollWidth, not the ul
         box — RO can't see it; the appender announces via event instead */
      this._onItems = () => this.requestSync();
      host.addEventListener('cc:items-changed', this._onItems);
      this._host = host;
      this.wireDrag();
      this.sync();
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      if (this.slider && this._onScroll) {
        this.slider.removeEventListener('scroll', this._onScroll);
      }
      if (this._host && this._onItems) {
        this._host.removeEventListener('cc:items-changed', this._onItems);
      }
    }

    requestSync() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.sync();
      });
    }

    sync() {
      const s = this.slider;
      if (!s || !this.track) return;
      const overflow = s.scrollWidth - s.clientWidth;
      if (overflow < 4) {
        this.classList.add('cc-cbar--idle');
        return;
      }
      this.classList.remove('cc-cbar--idle');
      const trackW = this.track.clientWidth;
      const thumbW = Math.min(
        trackW * 0.4,
        Math.max(40, (s.clientWidth / s.scrollWidth) * trackW)
      );
      const x = (s.scrollLeft / overflow) * (trackW - thumbW);
      this.thumb.style.width = thumbW + 'px';
      this.thumb.style.transform = 'translateX(' + x + 'px)';
    }

    wireDrag() {
      const s = this.slider;

      this.thumb.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.classList.add('cc-cbar--dragging');
        /* scroll-snap fights 1:1 dragging, and Dawn's .slider sets
           scroll-behavior:smooth (every scrollLeft write becomes a queued
           animation = dead drag) — both off for the drag, back on release */
        s.style.scrollSnapType = 'none';
        s.style.scrollBehavior = 'auto';
        const startX = e.clientX;
        const startLeft = s.scrollLeft;
        const move = (ev) => {
          const overflow = s.scrollWidth - s.clientWidth;
          const scrollable = this.track.clientWidth - this.thumb.offsetWidth;
          if (scrollable <= 0) return;
          s.scrollLeft = startLeft + ((ev.clientX - startX) / scrollable) * overflow;
        };
        const up = () => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          this.classList.remove('cc-cbar--dragging');
          s.style.scrollSnapType = '';
          s.style.scrollBehavior = '';
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      });

      this.track.addEventListener('pointerdown', (e) => {
        if (e.target === this.thumb) return;
        const rect = this.track.getBoundingClientRect();
        const thumbW = this.thumb.offsetWidth;
        const ratio = (e.clientX - rect.left - thumbW / 2) / (rect.width - thumbW);
        const overflow = s.scrollWidth - s.clientWidth;
        s.scrollTo({
          left: Math.max(0, Math.min(1, ratio)) * overflow,
          behavior: 'smooth',
        });
      });
    }
  }

  customElements.define('cc-carousel-bar', CCCarouselBar);
})();
